import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import { DateCell, NameCell } from '@/components/TableCells'
import { Team } from '@/services/users/teamsApi'

interface TeamTableProps {
  teams: Team[]
  loading?: boolean
  emptyMessage?: string
  selection?: Team[]
  onSelectionChange?: (teams: Team[]) => void
}

export function TeamTable({ teams, loading, emptyMessage, selection, onSelectionChange }: TeamTableProps) {
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<Team, unknown>[] = [
    ...(selectable ? [selectionColumn<Team>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'type',
      header: t('common.type'),
      meta: { minWidth: 140 },
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.type ? t(`teams.types.${row.original.type}`, row.original.type) : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'defaultCurrency',
      header: t('teams.currency'),
      size: 120,
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.defaultCurrency || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        row.original.objectState ? (
          <span
            className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
            style={{ fontSize: 11 }}
          >
            <span className="dot" />
            {row.original.objectState}
          </span>
        ) : (
          <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>—</span>
        )
      ),
    },
    optionalColumn<Team>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<Team>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={teams}
      loading={loading}
      emptyMessage={emptyMessage ?? t('teams.noTeamsFound')}
      columnsKey="teams"
      rowHref={(row) => `/admin/teams/${row.id}`}
      {...(selectable ? { selection, onSelectionChange } : {})}
    />
  )
}
